/**
 * @module config
 */
import type { CurrencyConfig } from './currencyConfig';
import CURRENCY, {
  SYMBOL_POSITION,
  DEFAULT_SEPARATOR_PATTERN,
} from './currencyConfig';
import {
  Code,
  CountryCode,
  Notch,
  CurrencyCode,
  LanguageCode,
  CountryConfig,
  type CurrencyCodeStrict,
} from '../typings';
import { isCurrencyCode, isCountryCode } from '../utils';
import { DEFAULT_NOTCHES } from './notchConfig';
import { getCountryConfig } from './countryConfig';

export { DEFAULT_NOTCHES } from './notchConfig';
export { CURRENCY as CURRENCY_CONFIG };

export { CurrencyConfig, SYMBOL_POSITION, DEFAULT_SEPARATOR_PATTERN };

/**
 * Get country config by countryCode
 * @since 	2.0.0
 * @param   {CountryCode} country		country code
 * @param   {LanguageCode} language		language code
 * @return 	{CountryConfig}
 */
export { getCountryConfig };

/**
 * Get currency config by countryCode or currencyId.
 * @since 	1.4.0
 * @param   {Code} code 	Can be country code or currency ID. If country code is provided, it will return this country's default currency setting.
 * @param   {LanguageCode} language The current language.
 * @return 	{?CurrencyConfig} returns currency config object with symbol, decimalPoint, convertrate....
 */
export function getCurrencyConfig(
  code: Code,
  language?: LanguageCode
): CurrencyConfig {
  if (isCurrencyCode(code)) {
    return CURRENCY[String(code).toUpperCase()];
  } else {
    const countryConf = getCountryConfig(code, language);
    const defaultCurrencyConfig =
      countryConf &&
      countryConf.defaultCurrency &&
      CURRENCY[countryConf.defaultCurrency];

    if (defaultCurrencyConfig) {
      return {
        ...defaultCurrencyConfig,
        ...countryConf.currencies[countryConf.defaultCurrency],
      };
    } else {
      return CURRENCY.default;
    }
  }
}

/**
 * Get currency symbol by country code
 * @since 2.0.0
 * @param   	{string} code 	Can be country code or currency ID. If country code is provided, it will return this country's default currency setting.
 * @return    {string}
 * @example
 * getCurrencySymbolByCountryCode('SG'); // '$'
 */
export function getCurrencySymbol(
  code: Code
): NonNullable<CurrencyConfig['symbol']> {
  const conf = getCurrencyConfig(code);
  return (conf && conf.symbol) || '';
}

/**
 *
 * @param {string} code Can be country code or currency ID. If country code is provided, it will return this country's default currency setting.
 * @returns {"FRONT"|"BACK"} "FRONT" means symbol be placed at the front of the number, "BACK" means symbol be placed at the back
 */
export function getCurrencySymbolPosition(
  code: Code
): NonNullable<CurrencyConfig['symbolPosition']> {
  const conf = getCurrencyConfig(code);
  return conf && typeof conf.symbolPosition !== 'undefined'
    ? conf.symbolPosition
    : SYMBOL_POSITION.FRONT;
}

/**
 *
 * @param {string} code Can be country code or currency ID. If country code is provided, it will return this country's default currency setting.
 * @returns {boolean} true means there should be a space between the number and the symbol
 */
export function getCurrencySpaceBetweenSymbol(
  code: Code
): NonNullable<CurrencyConfig['spaceBetweenSymbol']> {
  const conf = getCurrencyConfig(code);
  return (conf && conf.spaceBetweenSymbol) || false;
}

/**
 * Get notches by country code
 * @param {string} country
 * @return {Notch}
 */
export function getNotches(country?: string, language?: LanguageCode): Notch[] {
  if (!isCountryCode(country)) {
    return DEFAULT_NOTCHES;
  }
  if (language) {
    return getCountryConfig(country, language).notches;
  } else {
    return getCountryConfig(country).notches;
  }
}

/**
 * @since  	2.0.0
 * @param 	{?string} country
 * @param 	{?string} currencyId
 * @return  {number}
 */
export function getServerNumberConvertRatio(
  country?: CountryCode,
  currencyId?: CurrencyCode
): number {
  if (country) {
    const countryConf = getCountryConfig(country);
    const currencyConf =
      countryConf?.currencies[
        (currencyId as CurrencyCodeStrict) || countryConf.defaultCurrency
      ];
    if (currencyConf?.convertRate) {
      return currencyConf.convertRate;
    }
  }
  return 100000;
}

/**
 *
 * @deprecated
 * @param country
 * @param language
 * @return {*}
 */
export function getConfigByCountry(
  country: CountryCode,
  language?: LanguageCode
): {
  getCountryConfig: (
    countryCode: CountryCode,
    languageCode?: LanguageCode
  ) => CountryConfig;
  getServerNumberConvertRatio: typeof getServerNumberConvertRatio;
  getNotches: (countryCode?: string, languageCode?: LanguageCode) => Notch[];
  getCurrencySymbol: (countryCode?: Code) => string | readonly [string, string];
  DEFAULT_NOTCHES: typeof DEFAULT_NOTCHES;
  getCurrencyConfig: (code?: Code) => CurrencyConfig;
} {
  return {
    getCountryConfig: (
      countryCode?: CountryCode,
      languageCode?: LanguageCode
    ): CountryConfig =>
      getCountryConfig(countryCode || country, languageCode || language),
    getServerNumberConvertRatio,
    getNotches: (countryCode?: string, languageCode?: LanguageCode): Notch[] =>
      getNotches(countryCode || country, languageCode || language),
    getCurrencySymbol: (countryCode?: Code) =>
      getCurrencySymbol(countryCode || country),
    DEFAULT_NOTCHES,
    getCurrencyConfig: (countryCode?: Code): CurrencyConfig =>
      getCurrencyConfig(countryCode || country, language),
  };
}
